import { CheckCircle, MapPin, User, X } from "lucide-react";
import { useContext, useEffect, useState } from "react";
import DialogChatAPI from "../../api/dialogChatApi";
import EducationalHubApi from "../../api/educationalHubApi";
import Loader from "../../components/Loader/Loader";
import ModalWindow from "../../components/ModalWindow/ModalWindow";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { AvailableLangs, ISO639Lang } from "../../constans/languagesList";
import { ContextProvider } from "../../contextProvider";
import { EducationalHub } from "../../types/EducationalHub";
import { MultiLangText } from "../../utils/profileDiffUtils";
import Notify, { SuccesNotify } from "../../utils/toaster";
import { ReviewMessageModal } from "../ReviewSpecialists/components/ReviewMessageModal";
import s from "../Webinars/Webinars.module.css";
import HubListManagement from "./HubManagement/HubListManagement";

interface ReviewStats {
  totalPending: number;
  reviewedToday: number;
  averageReviewTime: number;
  queuePosition: number;
}

const getTextByLang = (
  values: MultiLangText[] | undefined,
  preferred?: string,
): string => {
  if (!values || values.length === 0) return "";
  const byPreferred = preferred
    ? values.find((item) => item.language === preferred)
    : undefined;
  return byPreferred?.text || values[0]?.text || "";
};

const getFirstLang = (values: MultiLangText[] | undefined): string => {
  if (!values || values.length === 0) return "en";
  return values[0]?.language || "en";
};

const EducationHubs = () => {
  const token = localStorage.getItem("token");
  const { userData } = useContext(ContextProvider);

  const [reviewQueue, setReviewQueue] = useState<EducationalHub[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoader, setIsLoader] = useState(false);
  const [logos, setLogos] = useState<{ [hubId: string]: string }>({});
  const [isLogoPreviewOpen, setIsLogoPreviewOpen] = useState(false);

  const [stats, setStats] = useState<ReviewStats>({
    totalPending: 0,
    reviewedToday: 0,
    averageReviewTime: 0,
    queuePosition: 0,
  });
  const [reviewTimes, setReviewTimes] = useState<number[]>([]);
  const [currentReviewStart, setCurrentReviewStart] = useState<number>(
    Date.now(),
  );

  const [isApprovalMessageModalOpen, setIsApprovalMessageModalOpen] =
    useState(false);
  const [isRejectionMessageModalOpen, setIsRejectionMessageModalOpen] =
    useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState<
    "approve" | "reject"
  >("approve");

  const currentHub = reviewQueue[currentIndex];
  const [activeTab, setActiveTab] = useState<"check" | "list">("check");

  useEffect(() => {
    loadReviewQueue();
  }, []);

  useEffect(() => {
    setStats((prev) => ({
      ...prev,
      totalPending: reviewQueue.length - currentIndex,
      queuePosition: currentIndex + 1,
    }));
  }, [reviewQueue, currentIndex]);

  useEffect(() => {
    setCurrentReviewStart(Date.now());
  }, [currentIndex]);

  useEffect(() => {
    const loadLogos = async () => {
      if (!token || reviewQueue.length === 0) return;

      const hubsToLoad = reviewQueue.slice(
        currentIndex,
        Math.min(currentIndex + 5, reviewQueue.length),
      );

      const signedLogoEntries = await Promise.all(
        hubsToLoad.map(async (hub) => {
          if (!hub.logo) return null;

          try {
            const response = await EducationalHubApi.getHubFileSignedUrl(
              token,
              hub.logo,
            );
            if (response.status && response.url) {
              return [hub._id, response.url] as const;
            }
          } catch (error) {
            console.error("Error loading hub logo:", error);
          }

          return null;
        }),
      );

      const logosToSet: { [hubId: string]: string } = {};
      signedLogoEntries.forEach((entry) => {
        if (!entry) return;
        logosToSet[entry[0]] = entry[1];
      });

      if (Object.keys(logosToSet).length > 0) {
        setLogos((prev) => ({ ...prev, ...logosToSet }));
      }
    };

    loadLogos();
  }, [reviewQueue, currentIndex, token]);

  const loadReviewQueue = async () => {
    if (!token) return;

    setIsLoader(true);
    try {
      const response = await EducationalHubApi.getUnapprovedHubs(token, {
        page: 1,
        limit: 1000,
      });

      if (response.status && response.hubs) {
        setReviewQueue(response.hubs);
        setStats((prev) => ({
          ...prev,
          totalPending: response.hubs!.length,
        }));
      } else {
        setReviewQueue([]);
      }
    } catch (error) {
      console.error("Error loading hub review queue:", error);
      Notify("Failed to load review queue");
    } finally {
      setIsLoader(false);
    }
  };

  const recordReviewTime = () => {
    const reviewTime = Date.now() - currentReviewStart;
    const newReviewTimes = [...reviewTimes, reviewTime];
    setReviewTimes(newReviewTimes);

    const avgTime =
      newReviewTimes.reduce((a, b) => a + b, 0) / newReviewTimes.length;
    setStats((prev) => ({
      ...prev,
      averageReviewTime: Math.round(avgTime / 1000),
      reviewedToday: prev.reviewedToday + 1,
    }));
  };

  const handleApproveClick = () => {
    setConfirmationAction("approve");
    setIsConfirmationModalOpen(true);
  };

  const handleRejectClick = () => {
    setConfirmationAction("reject");
    setIsConfirmationModalOpen(true);
  };

  const handleApproveWithMessage = async (message: string) => {
    if (!token || !currentHub || !userData) return;

    try {
      await sendAdminMessage(message);

      const response = await EducationalHubApi.updateHub(token, {
        id: currentHub._id,
        _id: currentHub._id,
        isApproved: true,
        isApproveRequested: false,
      });

      if (response.status) {
        recordReviewTime();
        setIsApprovalMessageModalOpen(false);
        SuccesNotify("Hub approved and owner notified");

        setReviewQueue((prev) => prev.filter((_, idx) => idx !== currentIndex));
      } else if (response.message) {
        Notify(response.message);
      }
    } catch (error) {
      console.error("Error approving hub:", error);
      Notify("Failed to approve hub");
    }
  };

  const handleRejectWithMessage = async (message: string) => {
    if (!token || !currentHub || !userData) return;

    try {
      await sendAdminMessage(message);

      const response = await EducationalHubApi.updateHub(token, {
        id: currentHub._id,
        _id: currentHub._id,
        isApproved: false,
        isApproveRequested: false,
      });

      if (response.status) {
        recordReviewTime();
        setIsRejectionMessageModalOpen(false);
        SuccesNotify("Hub rejected and owner notified with feedback");

        setReviewQueue((prev) => prev.filter((_, idx) => idx !== currentIndex));
      } else if (response.message) {
        Notify(response.message);
      }
    } catch (error) {
      console.error("Error rejecting hub:", error);
      Notify("Failed to reject hub");
    }
  };

  const sendAdminMessage = async (message: string) => {
    if (!token || !userData || !currentHub || !currentHub.createdBy) {
      return;
    }

    const chatResponse = await DialogChatAPI.createDialogChat(token, {
      userId: userData._id,
      userIdToSend: currentHub.createdBy._id,
    });

    if (!chatResponse.status) {
      throw new Error("Failed to create chat");
    }

    await DialogChatAPI.sendMessage({
      userIdToSend: currentHub.createdBy._id,
      message: message,
      userId: userData._id,
      date: new Date(),
      files: [],
    });
  };

  const moveToNext = () => {
    if (currentIndex < reviewQueue.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      loadReviewQueue();
      setCurrentIndex(0);
    }
  };

  const moveToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "—";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "Invalid date";
    }
  };

  const getOwnerName = (): string => {
    if (!currentHub?.createdBy) return "Owner";
    const firstName = Array.isArray(currentHub.createdBy.name)
      ? currentHub.createdBy.name[0]?.text
      : currentHub.createdBy.name || "";
    const lastName = Array.isArray(currentHub.createdBy.surname)
      ? currentHub.createdBy.surname[0]?.text
      : currentHub.createdBy.surname || "";
    return `${firstName} ${lastName}`.trim();
  };

  const getHubTitle = (hub: EducationalHub): string =>
    getTextByLang(hub.title, hub.createdBy?.selectedLanguage) || "Untitled hub";

  const getHubBriefDescription = (hub: EducationalHub): string =>
    getTextByLang(hub.briefDescription, hub.createdBy?.selectedLanguage);

  const getHubDescription = (hub: EducationalHub): string =>
    getTextByLang(hub.description, hub.createdBy?.selectedLanguage);

  const getHubDisplayLang = (hub: EducationalHub): string => {
    const lang = getFirstLang(hub.title);
    return ISO639Lang[lang] || lang;
  };

  const getCurrentHubLogo = (): string | undefined => {
    if (!currentHub) return undefined;
    return logos[currentHub._id];
  };

  const hubApprovalTemplates: {
    [key in AvailableLangs]?: (name: string, spec?: string) => string;
  } = {
    en: (name: string) => `Dear ${name},

Congratulations! Your Educational Hub "${getHubTitle(currentHub)}" has been reviewed and approved.

You can now continue publishing content and managing your team in the hub.

Thank you for building quality learning spaces on our platform!

Best regards,
Review Team`,

    ru: (name: string) => `Уважаемый(ая) ${name},

Поздравляем! Ваш образовательный хаб «${getHubTitle(currentHub)}» был проверен и одобрен.

Теперь вы можете публиковать контент и управлять командой в хабе.

Спасибо за создание качественной образовательной среды на нашей платформе!

С уважением,
Команда модерации`,

    uk: (name: string) => `Шановний(а) ${name},

Вітаємо! Ваш освітній хаб «${getHubTitle(currentHub)}» було перевірено та схвалено.

Тепер ви можете публікувати контент і керувати командою в хабі.

Дякуємо за створення якісного навчального простору на нашій платформі!

З повагою,
Команда модерації`,
  };

  const hubRejectionTemplates: {
    [key in AvailableLangs]?: (
      name: string,
      spec?: string,
      fields?: string,
    ) => string;
  } = {
    en: (name: string, reasons?: string) => {
      let msg = `Dear ${name},

Thank you for submitting your Educational Hub "${getHubTitle(currentHub)}" for review.

Unfortunately, we cannot approve this hub at the moment. Please review the feedback below and make the necessary changes before resubmitting:

`;
      if (reasons) {
        msg += reasons + "\n\n";
      } else {
        msg += "[Please specify the reasons for rejection]\n\n";
      }

      msg += `Once the issues are resolved, you may resubmit the hub for review.

Best regards,
Review Team`;

      return msg;
    },

    ru: (name: string, reasons?: string) => {
      let msg = `Уважаемый(ая) ${name},

Спасибо за отправку образовательного хаба «${getHubTitle(currentHub)}» на модерацию.

К сожалению, мы не можем одобрить данный хаб в текущем виде. Пожалуйста, ознакомьтесь с замечаниями ниже и внесите необходимые изменения перед повторной отправкой:

`;
      if (reasons) {
        msg += reasons + "\n\n";
      } else {
        msg += "[Пожалуйста, укажите причины отклонения]\n\n";
      }

      msg += `После внесения изменений вы сможете повторно отправить хаб на модерацию.

С уважением,
Команда модерации`;

      return msg;
    },

    uk: (name: string, reasons?: string) => {
      let msg = `Шановний(а) ${name},

Дякуємо за подання освітнього хаба «${getHubTitle(currentHub)}» на перевірку.

На жаль, ми не можемо схвалити цей хаб у поточному вигляді. Будь ласка, ознайомтеся із зауваженнями нижче та внесіть необхідні зміни перед повторним поданням:

`;
      if (reasons) {
        msg += reasons + "\n\n";
      } else {
        msg += "[Будь ласка, вкажіть причини відхилення]\n\n";
      }

      msg += `Після внесення змін ви зможете повторно подати хаб на перевірку.

З повагою,
Команда модерації`;

      return msg;
    },
  };

  const renderCheckHubs = () => {
    if (isLoader) {
      return (
        <div className={s.loaderContainer}>
          <Loader size={80} />
          <p className={s.loadingText}>
            Loading Educational Hub review queue...
          </p>
        </div>
      );
    }

    if (reviewQueue.length === 0) {
      return (
        <div className={s.emptyContainer}>
          <div className={s.emptyState}>
            <CheckCircle className={s.emptyIcon} size={64} />
            <h2 className={s.emptyTitle}>All Educational Hubs reviewed</h2>
            <p className={s.emptyText}>
              There are no Educational Hubs waiting for review at the moment.
            </p>
            {stats.reviewedToday > 0 && (
              <div className={s.statsCard}>
                <div className={s.sessionStats}>
                  <div className={s.statItem}>
                    <span className={s.statLabel}>Reviewed Today</span>
                    <span className={s.statValue}>{stats.reviewedToday}</span>
                  </div>
                  {stats.averageReviewTime > 0 && (
                    <div className={s.statItem}>
                      <span className={s.statLabel}>Avg Review Time</span>
                      <span className={s.statValue}>
                        {formatTime(stats.averageReviewTime)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
            <Button onClick={loadReviewQueue} className="mt-4">
              Refresh Queue
            </Button>
          </div>
        </div>
      );
    }

    return (
      <>
      <div className={s.header}>
        <div className={s.headerContent}>
          <div className={s.titleBlock}>
            <h1 className={s.title}>Educational Hub Review</h1>
            <Badge variant="destructive" className={s.pendingBadge}>
              {stats.totalPending} Pending
            </Badge>
          </div>
          <Button variant="outline" onClick={loadReviewQueue} size="sm">
            Refresh
          </Button>
        </div>

        <div className={s.progressContainer}>
          <div className={s.progressBar}>
            <div
              className={s.progressFill}
              style={{
                width: `${(currentIndex / reviewQueue.length) * 100}%`,
              }}
            />
          </div>
          <p className={s.progressText}>
            {stats.queuePosition} / {reviewQueue.length}
          </p>
        </div>
      </div>

      <div className={s.contentArea}>
        <div className={s.listPanel}>
          <div className={s.listHeader}>
            <h2 className={s.listTitle}>Queue ({reviewQueue.length})</h2>
          </div>
          <div className={s.webinarList}>
            {reviewQueue.map((hub, idx) => (
              <div
                key={hub._id}
                className={`${s.listItem} ${idx === currentIndex ? s.active : ""}`}
                onClick={() => setCurrentIndex(idx)}
              >
                <div className={s.listItemNumber}>{idx + 1}</div>
                <div className={s.listItemContent}>
                  <div className={s.listItemTitle}>{getHubTitle(hub)}</div>
                  <div className={s.listItemMeta}>
                    {getHubDisplayLang(hub)}
                    {hub.team?.length ? ` • ${hub.team.length} members` : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className={s.listNavigation}>
            <Button
              variant="outline"
              size="sm"
              onClick={moveToPrevious}
              disabled={currentIndex === 0}
              className={s.listNavBtn}
            >
              ← Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={moveToNext}
              className={s.listNavBtn}
            >
              Skip →
            </Button>
          </div>
        </div>

        {currentHub && (
          <div className={s.detailsPanel}>
            <div className={s.detailsHeader}>
              {getCurrentHubLogo() ? (
                <img
                  src={getCurrentHubLogo()}
                  alt={getHubTitle(currentHub)}
                  className={s.detailsAvatar}
                  onClick={() => setIsLogoPreviewOpen(true)}
                />
              ) : (
                <div className={s.detailsAvatarPlaceholder}>
                  <MapPin size={32} />
                </div>
              )}
              <div className={s.detailsHeaderContent}>
                <h2 className={s.detailsTitle}>{getHubTitle(currentHub)}</h2>
                <p className={s.detailsBy}>
                  <User size={14} />
                  {getOwnerName()}
                </p>
              </div>
            </div>

            <div className={s.detailsScroll}>
              <div className={s.contentSection}>
                <h3 className={s.sectionTitle}>Review Info</h3>
                <p className={s.sectionText}>
                  Requested At: {formatDate(currentHub.approveRequestedAt)}
                </p>
              </div>

              {getHubBriefDescription(currentHub) && (
                <div className={s.contentSection}>
                  <h3 className={s.sectionTitle}>Brief Description</h3>
                  <p className={s.sectionText}>
                    {getHubBriefDescription(currentHub)}
                  </p>
                </div>
              )}

              {getHubDescription(currentHub) && (
                <div className={s.contentSection}>
                  <h3 className={s.sectionTitle}>Description</h3>
                  <p className={s.sectionText}>
                    {getHubDescription(currentHub)}
                  </p>
                </div>
              )}

              {currentHub.team && currentHub.team.length > 0 && (
                <div className={s.contentSection}>
                  <h3 className={s.sectionTitle}>Hub Team</h3>
                  <ul className={s.bulletList}>
                    {currentHub.team.map((member) => {
                      const firstName = Array.isArray(member.user?.name)
                        ? member.user.name[0]?.text
                        : member.user?.name || "";
                      const lastName = Array.isArray(member.user?.surname)
                        ? member.user.surname[0]?.text
                        : member.user?.surname || "";

                      return (
                        <li key={member._id}>
                          {`${firstName} ${lastName}`.trim() ||
                            member.user?.email}{" "}
                          ({member.roles.join(", ")})
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>

            <div className={s.detailsActions}>
              <Button
                variant="destructive"
                onClick={handleRejectClick}
                className="flex-1"
              >
                Reject
              </Button>
              <Button
                onClick={handleApproveClick}
                className="flex-1"
                variant={"success"}
              >
                Approve
              </Button>
            </div>
          </div>
        )}
      </div>

      <ModalWindow
        isOpen={isConfirmationModalOpen}
        setIsOpen={setIsConfirmationModalOpen}
        title={
          confirmationAction === "approve" ? "Approve Hub?" : "Reject Hub?"
        }
        width="fit-content"
      >
        <div className={s.confirmationContent}>
          <p>
            You are about to{" "}
            <strong>
              {confirmationAction === "approve" ? "approve" : "reject"}
            </strong>{" "}
            this Educational Hub. The owner will be notified with a message.
          </p>
          <div className={s.confirmationActions}>
            <Button
              variant="outline"
              onClick={() => setIsConfirmationModalOpen(false)}
              className={s.cancelBtn}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setIsConfirmationModalOpen(false);
                if (confirmationAction === "approve") {
                  setIsApprovalMessageModalOpen(true);
                } else {
                  setIsRejectionMessageModalOpen(true);
                }
              }}
            >
              Continue
            </Button>
          </div>
        </div>
      </ModalWindow>

      <ModalWindow
        isOpen={isApprovalMessageModalOpen}
        setIsOpen={setIsApprovalMessageModalOpen}
        title="Send Approval Message"
        width="fit-content"
      >
        {currentHub && (
          <ReviewMessageModal
            isApproval={true}
            specialistName={getOwnerName()}
            onSend={handleApproveWithMessage}
            onCancel={() => setIsApprovalMessageModalOpen(false)}
            userSelectedLang={
              ((currentHub.createdBy?.selectedLanguage ||
                getFirstLang(currentHub.title)) as AvailableLangs) || "en"
            }
            approvalTemplatesProps={hubApprovalTemplates}
          />
        )}
      </ModalWindow>

      <ModalWindow
        isOpen={isRejectionMessageModalOpen}
        setIsOpen={setIsRejectionMessageModalOpen}
        title="Send Rejection Message"
        width="fit-content"
      >
        {currentHub && (
          <ReviewMessageModal
            isApproval={false}
            specialistName={getOwnerName()}
            onSend={handleRejectWithMessage}
            onCancel={() => setIsRejectionMessageModalOpen(false)}
            userSelectedLang={
              ((currentHub.createdBy?.selectedLanguage ||
                getFirstLang(currentHub.title)) as AvailableLangs) || "en"
            }
            rejectionTemplatesProps={hubRejectionTemplates}
          />
        )}
      </ModalWindow>

      {currentHub && getCurrentHubLogo() && isLogoPreviewOpen && (
        <div
          className={s.filePreviewOverlay}
          onClick={() => setIsLogoPreviewOpen(false)}
        >
          <div
            className={s.filePreviewModal}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={s.filePreviewHeader}>
              <h3 className={s.filePreviewTitle}>Hub Logo</h3>
              <button
                className={s.filePreviewClose}
                onClick={() => setIsLogoPreviewOpen(false)}
                aria-label="Close"
              >
                <X size={24} />
              </button>
            </div>

            <div className={s.filePreviewContent}>
              <img
                src={getCurrentHubLogo()}
                alt={getHubTitle(currentHub)}
                className={s.previewImage}
              />
            </div>
          </div>
        </div>
      )}
      </>
    );
  };

  return (
    <div className={s.mainLayout}>
      <div className={s.hubTabs}>
        <button
          type="button"
          className={`${s.hubTab} ${activeTab === "check" ? s.hubTabActive : ""}`}
          onClick={() => setActiveTab("check")}
        >
          Check Hubs
        </button>
        <button
          type="button"
          className={`${s.hubTab} ${activeTab === "list" ? s.hubTabActive : ""}`}
          onClick={() => setActiveTab("list")}
        >
          List of Hubs
        </button>
      </div>
      <div className={s.hubTabContent}>
        {activeTab === "check" ? renderCheckHubs() : <HubListManagement />}
      </div>
    </div>
  );
};

export default EducationHubs;
